export const CATEGORIES = [
  'slur',
  'sexual',
  'profanity',
  'violence',
  'harassment',
  'self_harm',
  'drugs',
  'impersonation',
] as const;

/** What kind of harm a word does; policies decide what each kind means in each place. */
export type Category = (typeof CATEGORIES)[number];

/** Words by category: `part` match anywhere, `word` only as a whole word. */
export type WordList = Partial<
  Record<Category, { part?: readonly string[]; word?: readonly string[] }>
>;

/**
 * What to do with text that has a word in it, mildest first:
 * let it through, hide the word (`***`), let it through for staff to look at, or refuse it.
 */
export const ACTIONS = ['allow', 'mask', 'review', 'block'] as const;
export type Action = (typeof ACTIONS)[number];

/** How strict one place is: usernames refuse anything; chat hides swearing and refuses slurs. */
export interface Policy {
  name: string;
  actions: Record<Category, Action>;
  /**
   * Whether a word may run across word breaks (`ThisHit` holds `shit`). Yes for names, where
   * any arrangement can be a disguise; no for sentences, where it would catch innocent ones.
   */
  acrossWords: boolean;
}

/** One harmful word found. `start` and `end` are UTF-16 offsets into the text checked. */
export interface Finding {
  category: Category;
  /** The listed word it matched. For logs and staff tools: never show it to the writer. */
  term: string;
  start: number;
  end: number;
  action: Action;
}

export interface Verdict {
  /** The strictest action among the findings; `allow` when there are none. */
  action: Action;
  findings: Finding[];
  categories: Category[];
  /** The text with every found word hidden behind `*`. */
  masked: string;
}
